import { AlertTriangle, Package } from "lucide-react";
import { useState, useMemo, useEffect } from "react";
import { SortingState, ColumnFiltersState } from "@tanstack/react-table";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { Autocomplete, Box, Checkbox } from "@mui/material";
import { Card, Row, Col, Spinner } from "react-bootstrap";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import CustomStyle, { autocompleteDropdownStyles } from "Constants/Style";
import DashboardTableContainer from "Common/Tables/ObjectFiltersTable/DashboardTableContainer";
import { fetchInventorySummary } from "slices/Dashboard/Inventory_Summary/thunk";
import { fetchCategoryByDropDown } from "slices/category/thunk";
import { api } from "helpers/api_url";
import { toast } from "react-toastify";
import { useWarehouseSelection } from "CustomHooks/useWarehouseSelection";
import "./InventorySummary.css";

export function InventorySummary() {
  const dispatch = useDispatch<any>();
  const [selectedWarehouse, setSelectedWarehouse] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [filterValue, setFilterValue] = useState<any[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [search, setSearch] = useState("");

  // Use custom hook for warehouse selection (only for fetching options, not for selection)
  const {
    warehouseOptions,
  } = useWarehouseSelection({
    sessionStorageKey: "WarehouseIdDispatch",
    autoSelectFirst: false, // Don't auto-select for dashboard
  });

  // Get category options from Redux store (same pattern as Sub-CategoryForm)
  const categoryOptionsFromStore = useSelector(
    (state: any) => state.Category.categoryDropdown
  );

  // Select inventory summary data from Redux store
  const selectInventorySummary = createSelector(
    (state: any) => state.InventorySummaryReducer,
    (data) => ({
      list: data.data,
      rowCount: data.totalRecords,
      low_stock_items_count: data.low_stock_items_count,
      total_stock: data.total_stock,
      loading: data.loading,
      error: data.error,
    })
  );

  const { list, rowCount, low_stock_items_count, total_stock, loading, error } =
    useSelector(selectInventorySummary);

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  // Fetch category dropdown options (same pattern as Sub-CategoryForm)
  useEffect(() => {
    const fetchDropdown = async () => {
      try {
        await dispatch(fetchCategoryByDropDown()).unwrap();
      } catch {
        toast.error("Failed to load categories");
      }
    };

    fetchDropdown();
  }, [dispatch]);

  // Normalize category options and add "All Categories" option
  const categoryOptions = useMemo(() => {
    const normalized = (categoryOptionsFromStore || []).map((item: any) => ({
      category_id: item?.category_id || item?.id,
      category_name: item?.category_name || item?.name || item || "",
      value: item?.category_id || item?.id || "",
    }));
    return [
      { category_id: null, category_name: "All Categories", value: "all" },
      ...normalized,
    ];
  }, [categoryOptionsFromStore]);

  // Build filters including warehouse and category dropdowns
  const allFilters = useMemo(() => {
    const filters = [...filterValue];

    // Always include warehouse_id filter, pass "all" if no warehouse is selected
    filters.push({
      filterField: "warehouse_id",
      filterValue:
        selectedWarehouse.length > 0
          ? selectedWarehouse.map((w) => w.warehouse_id).join(",")
          : "all",
    });

    if (selectedCategory !== "all") {
      filters.push({
        filterField: "category_id",
        filterValue: selectedCategory,
      });
    }

    return filters;
  }, [filterValue, selectedWarehouse, selectedCategory]);

  // Fetch inventory summary data
  useEffect(() => {
    dispatch(
      fetchInventorySummary({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: allFilters,
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    search,
    allFilters,
  ]);

  useEffect(() => {
    setPageIndex(0);
  }, [sorting, pageSize, columnFilters, selectedWarehouse, selectedCategory]);

  // Add className to rows based on status for styling
  useEffect(() => {
    const tableRows = document.querySelectorAll(
      ".inventory-summary-table-inner .table tbody tr"
    );
    tableRows.forEach((row) => {
      const statusCell = row.querySelector("[data-status]");
      if (statusCell) {
        const status = statusCell.getAttribute("data-status");
        if (status === "low") {
          row.classList.add("low-stock-row");
        } else {
          row.classList.remove("low-stock-row");
        }
      }
    });
  }, [list]);

  const getStatusBadge = (item: any) => {
    if (item?.status?.toLowerCase() === "low stock") {
      return (
        <span className="inventory-summary-badge inventory-summary-badge-low">
          <AlertTriangle className="inventory-summary-badge-icon" />
          Low Stock
        </span>
      );
    }
    return (
      <span className="inventory-summary-badge inventory-summary-badge-available">
        Available
      </span>
    );
  };

  const columns = useMemo(
    () => [
      {
        header: "Item Name",
        accessorKey: "item_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${
          api?.dashboad?.inventory_summary_list_filter_dropdown
        }/item_name?warehouse_id=${
          selectedWarehouse.length > 0
            ? selectedWarehouse.map((w) => w.warehouse_id).join(",")
            : "all"
        }&category_id=${selectedCategory !== "all" ? selectedCategory : "all"}`,
        cell: ({ row }: { row: any }) => {
          const item = row.original;
          const stock =
            item.total_available_quantity || item.available_quantity || 0;
          const reorderLevel = item.reorder_level || 0;
          const status =
            stock <= reorderLevel && stock > 0 ? "low" : "available";
          return (
            <div data-status={status}>
              <span className="inventory-summary-table-styles">
                {item.item_name || "-"}
              </span>
            </div>
          );
        },
      },
      {
        header: "Item Code",
        accessorKey: "item_code",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${
          api?.dashboad?.inventory_summary_list_filter_dropdown
        }/item_code?warehouse_id=${
          selectedWarehouse.length > 0
            ? selectedWarehouse.map((w) => w.warehouse_id).join(",")
            : "all"
        }&category_id=${selectedCategory !== "all" ? selectedCategory : "all"}`,
        cell: ({ row }: { row: any }) => {
          return (
            <span className="inventory-summary-table-styles">
              {row.original.item_code || "-"}
            </span>
          );
        },
      },
      {
        header: "Category",
        accessorKey: "category_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${
          api?.dashboad?.inventory_summary_list_filter_dropdown
        }/category_name?warehouse_id=${
          selectedWarehouse.length > 0
            ? selectedWarehouse.map((w) => w.warehouse_id).join(",")
            : "all"
        }&category_id=${selectedCategory !== "all" ? selectedCategory : "all"}`,
        cell: ({ row }: { row: any }) => {
          return (
            <span className="inventory-summary-table-styles">
              {row.original.category_name || "-"}
            </span>
          );
        },
      },
      {
        header: "Sub Category",
        accessorKey: "sub_category_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${
          api?.dashboad?.inventory_summary_list_filter_dropdown
        }/sub_category_name?warehouse_id=${
          selectedWarehouse.length > 0
            ? selectedWarehouse.map((w) => w.warehouse_id).join(",")
            : "all"
        }&category_id=${selectedCategory !== "all" ? selectedCategory : "all"}`,
        cell: ({ row }: { row: any }) => {
          return (
            <span className="inventory-summary-table-styles">
              {row.original.sub_category_name || "-"}
            </span>
          );
        },
      },
      {
        header: "Warehouse Name",
        accessorKey: "warehouse_name",
        enableSorting: false,
        enableColumnFilter: false,
        filterType: "dropdown",
        filterFetchUrl: `${
          api?.dashboad?.inventory_summary_list_filter_dropdown
        }/warehouse.warehouse_name ?warehouse_id=${
          selectedWarehouse.length > 0
            ? selectedWarehouse.map((w) => w.warehouse_id).join(",")
            : "all"
        }&category_id=${selectedCategory !== "all" ? selectedCategory : "all"}`,
        cell: ({ row }: { row: any }) => {
          return (
            <span className="inventory-summary-table-styles">
              {row.original.warehouse?.warehouse_name || "All Warehouse"}
            </span>
          );
        },
      },
      {
        header: "Reorder Level",
        accessorKey: "reorder_level",
        enableSorting: false,
        enableColumnFilter: false,
        filterType: "dropdown",
        filterFetchUrl: `${
          api?.dashboad?.inventory_summary_list_filter_dropdown
        }/reorder_level?warehouse_id=${
          selectedWarehouse.length > 0
            ? selectedWarehouse.map((w) => w.warehouse_id).join(",")
            : "all"
        }&category_id=${selectedCategory !== "all" ? selectedCategory : "all"}`,
        cell: ({ row }: { row: any }) => {
          return (
            <span className="inventory-summary-table-styles">
              {row.original?.reorder_level || "-"}
            </span>
          );
        },
      },
      {
        header: "Available Stock",
        accessorKey: "available_stock",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }: { row: any }) => {
          const item = row.original;
          // const stock =
          //   item.total_available_quantity || item.available_quantity || 0;
          // const reorderLevel = item.reorder_level || 0;
          // const isLowStock = stock <= reorderLevel && stock > 0;
          const isLowStock = item?.status?.toLowerCase() === "low stock";
          // const unit = item.unit_name || item.unit || "";
          return (
            <span
              className={`inventory-summary-table-styles ${
                isLowStock ? "low-stock" : ""
              }`}
            >
              {/* {stock.toLocaleString()} <span className="inventory-summary-stock-unit">{unit}</span> */}
              {row.original?.available_stock?.toLocaleString()}{" "}
              {/* <span className="inventory-summary-stock-unit">{unit}</span> */}
            </span>
          );
        },
      },
      {
        header: "Unit",
        accessorKey: "unit",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const item = row.original;
          const unit = item.unit_name || item.unit || "";
          return <span className="inventory-summary-table-styles">{unit}</span>;
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          return getStatusBadge(row.original);
        },
      },
    ],
    [selectedWarehouse, selectedCategory]
  );

  return (
    <Card className="inventory-summary-card shadow-sm border-0">
      <Card.Body className="p-4">
        {/* Header */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 pb-2 border-bottom position-relative gap-3 gap-md-0">
          <div className="order-summary-title-container">
            <Package className="order-summary-icon" />
            <h3 className="order-summary-title">Inventory Summary</h3>
          </div>

          {/* Filters - positioned at the end */}
          <div className="inventory-summary-filters d-flex flex-wrap gap-2 align-items-center" style={{ marginLeft: "auto" }}>
            <Autocomplete
              multiple
              options={warehouseOptions}
              value={selectedWarehouse}
              onChange={(event, newValue) => {
                setSelectedWarehouse(newValue);
                // Update sessionStorage with first selected warehouse (or clear if none)
                if (newValue.length > 0) {
                  sessionStorage.setItem("WarehouseIdDispatch", String(newValue[0].warehouse_id));
                } else {
                  sessionStorage.removeItem("WarehouseIdDispatch");
                }
              }}
              getOptionLabel={(option: any) => option?.warehouse_name || ""}
              isOptionEqualToValue={(option, value) =>
                option?.warehouse_id === value?.warehouse_id
              }
              disableClearable={false}
              disableCloseOnSelect={true} // Keep dropdown open after selection
              renderTags={() => null} // Hide chips in input box
              sx={{
                ...CustomStyle.multiselect,
                minWidth: { xs: "100%", sm: "280px" },
                maxWidth: { xs: "100%", sm: "350px" },
                width: { xs: "100%", sm: "auto" },
                "& .MuiInputBase-root": {
                  height: "38px",
                  fontSize: "13px",
                  fontFamily: "Poppins, sans-serif",
                  "& .MuiInputBase-input": {
                    height: "38px",
                    paddingTop: "0px",
                    paddingBottom: "0px",
                    display: "flex",
                    alignItems: "center",
                    transform: "translateY(-8px)",
                  },
                  "& .MuiInputBase-input::placeholder": {
                    textAlign: "left",
                    lineHeight: "38px",
                    display: "flex",
                    alignItems: "center",
                    margin: 0,
                  },
                },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "0.5rem",
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#65A19D",
                    borderWidth: "1.5px",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#65A19D",
                  },
                },
              }}
              renderOption={(props, option, { selected }) => {
                const { key, ...rest } = props;
                return (
                  <li
                    key={key}
                    {...rest}
                    style={{
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "13px",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        gap: 1,
                      }}
                    >
                      <Checkbox
                        checked={selected}
                        sx={{
                          color: "#65A19D",
                          "&.Mui-checked": {
                            color: "#65A19D",
                          },
                          padding: "4px",
                        }}
                      />
                      <span>{option?.warehouse_name || ""}</span>
                    </Box>
                  </li>
                );
              }}
              ListboxProps={autocompleteDropdownStyles.ListboxProps}
              renderInput={(params) => (
                <BootstrapTextField
                  {...params}
                  placeholder={
                    selectedWarehouse.length > 0
                      ? `${selectedWarehouse.length} warehouse${
                          selectedWarehouse.length > 1 ? "s" : ""
                        } selected`
                      : "All Warehouses"
                  }
                  sx={autocompleteDropdownStyles.inputSx}
                />
              )}
            />
            <Autocomplete
              options={categoryOptions}
              value={
                selectedCategory === "all"
                  ? categoryOptions.find((x) => x.value === "all") || null
                  : categoryOptions.find(
                      (x) => x.category_id === selectedCategory
                    ) || null
              }
              onChange={(e, val) => {
                setSelectedCategory(
                  val?.value === "all" ? "all" : val?.category_id || "all"
                );
              }}
              getOptionLabel={(option: any) => option?.category_name || ""}
              sx={{ 
                ...CustomStyle.singleselect, 
                minWidth: { xs: "100%", sm: 220 },
                maxWidth: { xs: "100%", sm: 300 },
                width: { xs: "100%", sm: "auto" }
              }}
              renderOption={(props, option) =>
                autocompleteDropdownStyles.renderOption(
                  props,
                  option,
                  (opt) => opt?.category_name || ""
                )
              }
              ListboxProps={autocompleteDropdownStyles.ListboxProps}
              renderInput={(params) => (
                <BootstrapTextField
                  {...params}
                  placeholder="All Categories"
                  size="small"
                />
              )}
            />
          </div>
        </div>

        {/* Summary Stats */}
        <Row className="inventory-summary-stats mb-4">
          {[
            {
              label: "Total Items",
              colorClass: "inventory-summary-stat-card-indigo",
              labelColorClass: "inventory-summary-stat-label-indigo",
              getValue: () => rowCount,
            },
            {
              label: "Available Stock Items",
              colorClass: "inventory-summary-stat-card-green",
              labelColorClass: "inventory-summary-stat-label-green",
              getValue: () =>
                Number(rowCount) - Number(low_stock_items_count) || 0,
            },
            {
              label: "Low Stock Items",
              colorClass: "inventory-summary-stat-card-red",
              labelColorClass: "inventory-summary-stat-label-red",
              getValue: () => low_stock_items_count || 0,
            },
          ].map((stat, index) => (
            <Col md={4} className="mb-0" key={index}>
              <Card
                className={`inventory-summary-stat-card ${stat.colorClass} border-0 shadow-sm h-100`}
              >
                <Card.Body className="d-flex flex-column justify-content-center text-center p-2">
                  <p
                    className={`inventory-summary-stat-label ${stat.labelColorClass} mb-1`}
                  >
                    {stat.label}
                  </p>
                  {loading ? (
                    <p className="inventory-summary-stat-value">
                      <Spinner
                        animation="border"
                        size="sm"
                        className="text-primary"
                      />
                    </p>
                  ) : (
                    <p className="inventory-summary-stat-value">
                      {stat.getValue()}
                    </p>
                  )}
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Inventory Table */}
        <div className="inventory-summary-table-wrapper">
          <div className="inventory-summary-table-container">
            <div className="inventory-summary-table-inner">
              <DashboardTableContainer
                columns={columns}
                data={loading ? [] : list}
                totalRecords={rowCount}
                pageIndex={pageIndex}
                pageSize={pageSize}
                onPageChange={setPageIndex}
                onPageSizeChange={setPageSize}
                columnFilters={columnFilters}
                setColumnFilters={setColumnFilters}
                colValue={setFilterValue}
                sorting={sorting}
                onSortingChange={setSorting}
                isBordered={false}
                isPagination={!loading && !error}
                isGlobalFilter={false}
                SearchPlaceholder="Search ..."
                manualSorting={true}
                loading={loading}
                error={error}
              />
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}
